import {
  CustomerWebsiteLayoutUI,
  SearchUI,
  TabsNavUI,
} from '@next-order/next-order-components';
import {
  setState as setPropsState,
  toggleMobileCart,
} from 'containers/Home/actions';
import ReviewOrder from 'containers/ReviewOrder';
import { MenuContext } from 'contexts/MenuProvider';
import {
  BREAKPOINT_VALUES,
  LOGIN_FROM_TYPE,
  ORDER_STAGE_TYPES,
} from 'helpers/enums';
import useMenuItems from 'helpers/hooks/useMenuItems';
import useSlider from 'helpers/hooks/useSlider';
import { useLoyaltyRoute } from 'helpers/hooks/useLoyaltyRoute';

import {
  createRef,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { isMobile } from '../../helpers/isMobile';
import RenderFilterAppliedSpecialMenuItems from './RenderFilterAppliedSpecialMenuItems';
import RenderHalfViewMenuItems from './RenderHalfViewMenuItems';
import RenderSimpleAndSpecialMenuItems from './RenderSimpleAndSpecialMenuItems';
import RenderVoucherFilterAppliedMenuItems from './RenderVoucherFilterAppliedMenuItems';
import getImage from 'helpers/getImage';
import FloatingNavbarMobile, {
  onClickDefaultState,
} from 'components/Header/FloatingNavbarMobile';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { useAuth } from 'contexts/AuthProvider';
import {
  PAGE_TRANSITION_DURATION,
  SIDEBAR_ANIMATION_DURATION,
} from 'containers/Home/constants';
import {
  getStoreLoyaltySettingsUrl,
  fetchStoreLoyaltySettingsSWR,
  fetchStoreLoyaltySettingsSuccess,
  attemptAutoEnrollAfterAuth,
} from 'components/Loyaltyv2/actions';
import { isAutomaticEnrollmentType } from 'components/Loyaltyv2/enrollmentType';

export default function NewMenuItems({
  halfView,
  halfItem,
  hhActiveMenuSizes,
  itemVariants,
  selectedHalfItems,
  getOrderBar,
  onClickItemHandler,
  updateOrderDeliveryAddress,
  saveUnit,
  signOut,
  hideOrder,
  itemInProgress,
  voucherItem,
  specialItem,
}) {
  const dispatch = useDispatch();
  const { selectedMenuItemsLength, breakPoint, setSearchModalOpen } =
    useContext(MenuContext);

  const {
    isOrderAgainAvailable,
    categoriesList,
    filteredCategoriesList,
    isSpecialFilterApplied,
    halfHalfSetup,
    spSubModifiers,

    menuItems,
    storeConfig,
    orderSetup,
    currentOrder,
    mobileCart,
    currentUser,
    userDetails,
    isVoucherFilterApplied,
  } = useMenuItems({
    halfView,
    halfItem,
    hhActiveMenuSizes,
    itemVariants,
    selectedHalfItems,
  });
  const { bookingOpen, loyaltyOpen, aboutOpen, profileOpen } = useSelector(
    (state) => state.homeReducer
  );
  const [activeTabID, setActiveTabID] = useState(isOrderAgainAvailable ? 0 : 1);
  const [isLoyaltyEnabled, setIsLoyaltyEnabled] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  const { authOrderStage, setState: setAuthState } = useAuth();
  const { userLoyaltyDetails, storeLoyaltySettings, isShowEnrollmentForm } =
    useSelector(
    (state) => state.loyaltyReducer
  );
  const { referralId } = useLoyaltyRoute();
  const resolveLoyaltyVisibility = (settings) =>
    Boolean(settings?.isActive) && Boolean(settings?.isLoyaltyLive);

  // Check if loyalty is enabled for this store
  useEffect(() => {
    const checkLoyaltySettings = async () => {
      // If we already have loyalty settings in Redux, use them
      if (storeLoyaltySettings !== null && storeLoyaltySettings !== undefined) {
        setIsLoyaltyEnabled(resolveLoyaltyVisibility(storeLoyaltySettings));
        return;
      }

      // Otherwise, make API call to get loyalty settings
      if (!storeConfig?.restaurantId || !storeConfig?.storeId) {
        return;
      }

      try {
        const storeLoyaltySettingsUrl = getStoreLoyaltySettingsUrl(
          storeConfig.restaurantId,
          storeConfig.storeId
        );

        if (storeLoyaltySettingsUrl) {
          const loyaltySettings = await fetchStoreLoyaltySettingsSWR(
            storeLoyaltySettingsUrl
          );

          // Dispatch to Redux so other components can use it
          if (loyaltySettings) {
            dispatch(fetchStoreLoyaltySettingsSuccess(loyaltySettings));
          }

          setIsLoyaltyEnabled(resolveLoyaltyVisibility(loyaltySettings));
        }
      } catch (error) {
        console.error('❌ Error checking loyalty settings:', error);
        setIsLoyaltyEnabled(false);
      }
    };

    checkLoyaltySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeConfig?.restaurantId, storeConfig?.storeId, storeLoyaltySettings]);

  useEffect(() => {
    const autoEnrollLoyaltyAccount = async () => {
      try {
        if (
          userDetails?._id &&
          storeLoyaltySettings?.id &&
          storeConfig?.storeId &&
          storeConfig?.restaurantId &&
          isLoyaltyEnabled &&
          isAutomaticEnrollmentType(storeLoyaltySettings.enrollmentType)
        ) {
          await dispatch(
            attemptAutoEnrollAfterAuth({
              userDetails,
              storeConfig,
              referralId,
            })
          );
        }
      } catch (error) {
        console.error('Failed to auto-enroll loyalty user:', error);
      }
    };
    autoEnrollLoyaltyAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userDetails?._id,
    storeLoyaltySettings?.id,
    isLoyaltyEnabled,
    storeConfig?.storeId,
    storeConfig?.restaurantId,
  ]);

  const allTabs = useMemo(() => {
    return categoriesList
      ?.sort((a, b) => a.displayOrder - b.displayOrder)
      .map((category, id) => {
        let categoryId = id;
        if (!isOrderAgainAvailable) {
          categoryId = id + 1;
        }

        return {
          id: categoryId,
          title: category.name,
        };
      });
  }, [categoriesList, isOrderAgainAvailable]);

  const allRefs = useMemo(
    () => allTabs.map(() => createRef()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allTabs?.length]
  );

  const scrollToCategory = useSlider({
    allRefs,
    activeTabID,
    setActiveTabID,
    marginTop: -20,
    rootMargin: '-150px',
    isOrderAgainAvailable,
    orderStage: currentOrder?.stage,
  });

  // We should show all Menu Items if specials view of halfhalf view is not selected
  const showMenuItems =
    !isSpecialFilterApplied && !halfView && !isVoucherFilterApplied;
  const showReviewOrderScreen =
    currentOrder &&
    currentOrder.stage &&
    currentOrder.stage >= ORDER_STAGE_TYPES.REVIEW &&
    currentOrder.isAllowToConfirm;
  const showSignOutButton =
    (currentUser?.phoneNumber || userDetails?.mobileNumber) &&
    userDetails?.firstName;
  const showBookingButton =
    currentOrder &&
    currentOrder.stage < ORDER_STAGE_TYPES.REVIEW &&
    orderSetup?.isTableBookingOn &&
    // Only show button when orderSetup is fully loaded (has useCustomTableBooking key)
    'useCustomTableBooking' in orderSetup;

  const showLoyaltyButton =
    (!currentOrder || currentOrder?.stage < ORDER_STAGE_TYPES.REVIEW) &&
    isLoyaltyEnabled;

  // We only show back button if specials view is selected or halfhalf view is selected
  const showBackBtn =
    !showMenuItems && ((isSpecialFilterApplied && !halfView) || halfView);

  const handleOverlayClick = () => {
    dispatch(toggleMobileCart(false));
    setTimeout(() => {
      setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
      dispatch(
        setPropsState({
          ...onClickDefaultState,
          currentOrder: {
            ...currentOrder,
            stage: ORDER_STAGE_TYPES.NONE,
          },
        })
      );
    }, PAGE_TRANSITION_DURATION);
  };

  const showOverlay = useMemo(() => {
    return (
      mobileCart &&
      (loyaltyOpen || authOrderStage === ORDER_STAGE_TYPES.OAUTH_FORM) &&
      (!userLoyaltyDetails || isShowEnrollmentForm)
    );
  }, [
    loyaltyOpen,
    authOrderStage,
    userLoyaltyDetails,
    isShowEnrollmentForm,
    mobileCart,
  ]);

  // Force View Order button visibility when cart closes (works for both cross button and browser back)
  const prevMobileCartRef = useRef(mobileCart);

  useEffect(() => {
    if (!isMobile() || !showMenuItems || selectedMenuItemsLength === 0) return;

    // When cart closes (mobileCart changes from true to false), focus the View Order button
    const cartJustClosed =
      prevMobileCartRef.current === true && mobileCart === false;

    if (cartJustClosed) {
      // Force CustomerWebsiteLayoutUI to re-render by updating key
      setLayoutKey((prev) => prev + 1);

      // Wait for component to re-render, then focus the button
      setTimeout(() => {
        const allButtons = Array.from(document.querySelectorAll('button'));
        const viewOrderButton = allButtons.find((btn) => {
          const text = btn.textContent?.trim().toLowerCase() || '';
          return text.includes('view order') || text.includes('vieworder');
        });

        if (viewOrderButton) {
          viewOrderButton.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
          });
          viewOrderButton.focus({ preventScroll: true });
          setTimeout(() => {
            if (document.activeElement === viewOrderButton) {
              viewOrderButton.blur();
            }
          }, 200);
        }
      }, 600);
    }

    prevMobileCartRef.current = mobileCart;
  }, [mobileCart, showMenuItems, selectedMenuItemsLength]);

  return (
    <>
      {showOverlay && (
        <div className='loyalty-overlay' onClick={handleOverlayClick} />
      )}
      <CustomerWebsiteLayoutUI
     //  key={layoutKey}
        showBackBtn={showBackBtn}
        onClickBackBtn={hideOrder}
        openOrderBar={mobileCart} //  toggle order bar on screen smaller  1024px
        onOpenChangeOrderBar={() => dispatch(toggleMobileCart(true))}
        orderBarAnimation={'fadeIn'}
        orderBarAnimationDuration={SIDEBAR_ANIMATION_DURATION}
        logoSrc={getImage(storeConfig.logoPath || storeConfig.logo)}
        logoAlt={storeConfig.name}
        orderBar={getOrderBar()}
        showViewOrderBtn={showMenuItems && selectedMenuItemsLength > 0}
        viewOrderBtnCount={selectedMenuItemsLength}
        viewOrderBtnText='View Order'
        onClickSearchBtn={setSearchModalOpen}
        extendedClassName={`${
          showReviewOrderScreen ? 'overflow-hidden ' : ''
        } `}
        extendedMainBtnClassName='!bottom-16 !bg-transparent shadow-none'
        extendedSidebarClassName={classNames({
          loyaltyGuestOpen:
            mobileCart &&
            loyaltyOpen &&
            (!userLoyaltyDetails || isShowEnrollmentForm),
          authOpen:
            mobileCart && authOrderStage === ORDER_STAGE_TYPES.OAUTH_FORM,
          '!transition-none': authOrderStage === ORDER_STAGE_TYPES.OAUTH_FORM,
        })}
        headerButtons={
          <HeaderButtons
            orderSetup={orderSetup}
            showSignOutButton={showSignOutButton}
            showBookingButton={showBookingButton}
            showLoyaltyButton={showLoyaltyButton}
            signOut={signOut}
            orderStage={currentOrder?.stage}
          />
        }
        headerSearch={
          !showReviewOrderScreen ? (
            <SearchUI
              extendedClassName='max-w-60.75 w-full hidden cursor-pointer md:block'
              onClick={() => setSearchModalOpen(true)}
              placeholder='Search Menu'
              s=''
              onChange={(e) => null}
              autoComplete='off'
              styleType='customer-website'
            />
          ) : null
        }
        headerTabs={
          !showReviewOrderScreen && showMenuItems ? (
            <TabsNavUI
              activeTabID={activeTabID}
              extendedClassName={'-mx-5 !text-primary-title'}
              extendedBtnClassName='!pb-2 !mr-6 text-3.75 leading-5'
              extendedLineClassName='!h-[2.5px]'
              fontSize='custom'
              fontWeight='semibold'
              setActiveTabID={(_, id) => scrollToCategory(id)}
              tabsList={allTabs}
              showScrollBtn={!isMobile()}
            />
          ) : null
        }
      >
        {/* =====================>Menu Items List<====================== */}
        {showReviewOrderScreen ? (
          <ReviewOrder
            updateOrderDeliveryAddress={updateOrderDeliveryAddress}
            saveUnit={saveUnit}
          />
        ) : (
          <MenuItems
            categoriesList={categoriesList}
            allRefs={allRefs}
            filteredCategoriesList={filteredCategoriesList}
            halfHalfSetup={halfHalfSetup}
            halfItem={halfItem}
            hhActiveMenuSizes={hhActiveMenuSizes}
            itemVariants={itemVariants}
            menuItems={menuItems}
            onClickItemHandler={onClickItemHandler}
            spSubModifiers={spSubModifiers}
            storeConfig={storeConfig}
            showMenuItems={showMenuItems}
            isSpecialFilterApplied={isSpecialFilterApplied}
            halfView={halfView}
            breakPoint={breakPoint}
            stage={currentOrder.stage}
            selectedMenuItemsLength={selectedMenuItemsLength}
            isVoucherFilterApplied={isVoucherFilterApplied}
          />
        )}
        {/* =====================>Menu Items List<====================== */}
      </CustomerWebsiteLayoutUI>
      {breakPoint <= BREAKPOINT_VALUES.DESKTOP &&
        currentOrder.stage < ORDER_STAGE_TYPES.REVIEW &&
        isEmpty(halfItem) &&
        isEmpty(voucherItem) &&
        isEmpty(specialItem) &&
        (!mobileCart ||
          aboutOpen ||
          bookingOpen ||
          loyaltyOpen ||
          profileOpen ||
          authOrderStage === ORDER_STAGE_TYPES.OAUTH_FORM) && (
          <FloatingNavbarMobile showLoyaltyButton={showLoyaltyButton} />
        )}
    </>
  );
}
// =====================>Header Buttons<======================

function HeaderButtons({
  orderSetup,
  showSignOutButton,
  showBookingButton,
  showLoyaltyButton,
  orderStage,
}) {
  const dispatch = useDispatch();
  const { setState: setAuthState, loading: authLoading } = useAuth();
  const onButtonClick = (key) => {
    if (!key) {
      dispatch(toggleMobileCart(true));
      setAuthState({
        authOrderStage: ORDER_STAGE_TYPES.OAUTH_FORM,
        loginFrom: LOGIN_FROM_TYPE.PROFILE,
      });
      dispatch(setPropsState({ ...onClickDefaultState }));
      return;
    }
    dispatch(toggleMobileCart(true));
    dispatch(setPropsState({ ...onClickDefaultState, [key]: true }));
  };

  return (
    <>
      <CustomerWebsiteLayoutUI.HeaderButton
        onClick={() => onButtonClick('aboutOpen')}
      >
        About
      </CustomerWebsiteLayoutUI.HeaderButton>
      {showLoyaltyButton && (
        <CustomerWebsiteLayoutUI.HeaderButton
          onClick={() => onButtonClick('loyaltyOpen')}
        >
          Loyalty
        </CustomerWebsiteLayoutUI.HeaderButton>
      )}
      {showBookingButton && (
        <CustomerWebsiteLayoutUI.HeaderButton
          onClick={() => {
            if (
              orderSetup.useCustomTableBooking === true &&
              orderSetup.customBookingURL !== ''
            ) {
              window.open(orderSetup.customBookingURL, '_blank');
            } else {
              onButtonClick('bookingOpen');
            }
          }}
        >
          Booking
        </CustomerWebsiteLayoutUI.HeaderButton>
      )}

      {authLoading ? (
        // Show skeleton loader while checking authentication - use actual HeaderButton to match size
        <CustomerWebsiteLayoutUI.HeaderButton
          disabled
          style={{ opacity: 0.5, cursor: 'default' }}
        >
          <span style={{ color: 'rgb(229, 231, 235)' }}>Profile</span>
        </CustomerWebsiteLayoutUI.HeaderButton>
      ) : showSignOutButton ? (
        orderStage < ORDER_STAGE_TYPES.REVIEW && (
          <CustomerWebsiteLayoutUI.HeaderButton
            onClick={() => onButtonClick('profileOpen')}
          >
            Profile
          </CustomerWebsiteLayoutUI.HeaderButton>
        )
      ) : (
        <CustomerWebsiteLayoutUI.HeaderButton
          onClick={() => onButtonClick(false)}
        >
          Log in
        </CustomerWebsiteLayoutUI.HeaderButton>
      )}
    </>
  );
}

// =====================>Menu Items<======================
function MenuItems({
  categoriesList,
  allRefs,
  filteredCategoriesList,
  halfHalfSetup,
  halfItem,
  hhActiveMenuSizes,
  itemVariants,
  menuItems,
  onClickItemHandler,
  spSubModifiers,

  storeConfig,
  showMenuItems,
  isSpecialFilterApplied,
  isVoucherFilterApplied,
  halfView,
  breakPoint,
  stage,
  selectedMenuItemsLength,
}) {
  return (
    <>
      {showMenuItems ? (
        <RenderSimpleAndSpecialMenuItems
          categoriesList={categoriesList}
          allRefs={allRefs}
          currency={storeConfig.currency}
          onClickItemHandler={onClickItemHandler}
        />
      ) : isSpecialFilterApplied && !halfView ? (
        <RenderFilterAppliedSpecialMenuItems
          filteredCategories={filteredCategoriesList}
          halfHalfSetup={halfHalfSetup}
          onClickItemHandler={onClickItemHandler}
          spSubModifiers={spSubModifiers}
          currency={storeConfig.currency}
        />
      ) : isVoucherFilterApplied && !halfView ? (
        <RenderVoucherFilterAppliedMenuItems
          filteredCategories={filteredCategoriesList}
          halfHalfSetup={halfHalfSetup}
          onClickItemHandler={onClickItemHandler}
          spSubModifiers={spSubModifiers}
          currency={storeConfig.currency}
        />
      ) : halfView ? (
        <RenderHalfViewMenuItems
          halfItem={halfItem}
          hhActiveMenuSizes={hhActiveMenuSizes}
          itemVariants={itemVariants}
          menuItems={menuItems}
          onClickItemHandler={onClickItemHandler}
          currency={storeConfig.currency}
        />
      ) : null}
    </>
  );
}
